import type { paths } from '@src/types/api'
import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { logger } from '@app/utils/logger'
import { uploadPartToS3 } from '@app/services/dataUploadService'

const UPLOAD_CHUNK_SIZE = 50 * 1024 * 1024 // 50 MB per part

type InitiateUploadResponse =
  paths['/evidence/documents/upload/initiate']['post']['responses'][200]['content']['application/json']
type CompleteUploadResponse =
  paths['/evidence/documents/upload/complete']['post']['responses'][200]['content']['application/json']
type CompleteUploadRequestBody = NonNullable<
  paths['/evidence/documents/upload/complete']['post']['requestBody']
>['content']['application/json']

export interface UploadedFileRef {
  file_key: string
  original_filename: string
}

async function initiateUpload(file: File, totalParts: number): Promise<InitiateUploadResponse> {
  const { data } = await apiClient.post<InitiateUploadResponse>(Endpoint.documentsUploadInitiate, {
    filename: file.name,
    content_type: file.type || undefined,
    total_parts: totalParts,
  })
  return data
}

async function completeUpload(
  key: string,
  s3UploadId: string,
  parts: CompleteUploadRequestBody['parts'],
): Promise<CompleteUploadResponse> {
  const { data } = await apiClient.post<CompleteUploadResponse>(Endpoint.documentsUploadComplete, {
    key,
    s3_upload_id: s3UploadId,
    parts,
  })
  return data
}

async function abortUpload(key: string, s3UploadId: string): Promise<void> {
  await apiClient.post(Endpoint.documentsUploadAbort, { key, s3_upload_id: s3UploadId })
}

// No direct-bytes path, every file goes through initiate/parts/complete,
export async function uploadDocumentFile(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<UploadedFileRef> {
  const totalParts = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE))
  const {
    key,
    s3_upload_id: s3UploadId,
    presigned_urls: presignedUrls,
  } = await initiateUpload(file, totalParts)

  if (!presignedUrls?.length) {
    throw new Error('Upload initiate response missing presigned URLs')
  }

  try {
    let completedParts = 0
    const parts = await Promise.all(
      presignedUrls.map(async (url, index) => {
        const chunk = file.slice(index * UPLOAD_CHUNK_SIZE, (index + 1) * UPLOAD_CHUNK_SIZE)
        const etag = await uploadPartToS3(url, chunk)
        completedParts += 1
        onProgress?.(Math.round((completedParts / presignedUrls.length) * 100))
        return { PartNumber: index + 1, ETag: etag }
      }),
    )

    const { file_key } = await completeUpload(key, s3UploadId, parts)
    return { file_key, original_filename: file.name }
  } catch (error) {
    logger.error('Error uploading document file, aborting:', error)
    await abortUpload(key, s3UploadId).catch((abortError) =>
      logger.error('Error aborting document upload:', abortError),
    )
    throw error
  }
}
